import { useEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import {
	useAutomationTemplateAllowUnsubscribedMutation,
	useAutomationTemplateSendToHouseholdMutation,
	useCancelCriteriaContactFilterMutation,
} from '../../../../queries';
import { AdvancedFiltersContextProvider } from '../../../components-v2/AdvancedFilters/AdvancedFiltersContext';
import { baseStyleSheet } from '../../../styles/styles';
import { AdvancedFiltersLegacyUI } from '../../AdvancedFilters/AdvancedFiltersLegacyUI';
import { Checkbox } from '../../Checkbox';
import { Collapsible } from '../../Collapsible';
import { FlyoutModal } from '../../FlyoutModal';
import { LoadingSpinner } from '../../LoadingSpinner';
import { AutomationTriggerIcon } from '../../svgs/icons/AutomationTriggerIcon';
import { AdvancedOptionsButton } from '../AutomationStepCard/presentation';
import { AutomationCancelOnReplyCheckbox, AutomationTriggerSettings } from './presentation';
import { styleSheet } from './styles';

interface IProps {
	automationTemplate?: Api.AutomationTemplateViewModel;
	className?: string;
	steps?: Api.AutomationTemplateEditorStep[];
	styles?: StyleDeclarationValue[];
}

interface IState {
	trigger?: Api.IAutomationTrigger;
	advancedOptionsToggled: boolean;
	advancedCancelOptions: boolean;
	cancelCriteria: Api.IContactFilterCriteria[];
	viewCriteriaFlyout: boolean;
}
const _AutomationTriggerCard = (props: IProps) => {
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const { className, styles, automationTemplate } = props;
	const { logApiError } = useEventLogging('AutomationTriggerCard');
	const sendToHouseholdMutation = useAutomationTemplateSendToHouseholdMutation({
		onError: error => {
			logApiError('AutomationTriggerCard', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: data => {
			automationTemplate.mModel = data;
		},
	});
	const allowUnsubscribedMutation = useAutomationTemplateAllowUnsubscribedMutation({
		onError: error => {
			logApiError('AutomationTriggerCard', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: data => {
			automationTemplate.mSetModel(data);
		},
	});
	const updateCriteria = useCancelCriteriaContactFilterMutation({
		impersonationContext: automationTemplate?.impersonationContext,
		onSuccess: () => {
			toast.success('Automation cancel criteria updated successfully');
		},
		onError: (error: any) => {
			errorMessages.pushApiError(error);
			logApiError('UpdateCancelCriteriaContactFilter-Error', error);
		},
	});

	const [state, setState] = React.useState<IState>({
		trigger: undefined,
		advancedOptionsToggled: false,
		advancedCancelOptions: false,
		cancelCriteria: [],
		viewCriteriaFlyout: false,
	});

	const trigger = React.useMemo(() => {
		return automationTemplate?.draftTriggerReference || automationTemplate?.publishedTriggerReference;
	}, [automationTemplate?.draftTriggerReference, automationTemplate?.publishedTriggerReference]);

	const onViewCriteria = React.useCallback((val: boolean) => {
		updateState({
			viewCriteriaFlyout: val,
		});
	}, []);

	React.useEffect(() => {
		if (automationTemplate?.contactFilter?.criteria?.length) {
			updateState({
				advancedOptionsToggled: true,
				advancedCancelOptions: true,
				cancelCriteria: automationTemplate.contactFilter.criteria,
			});
		}
	}, [automationTemplate?.contactFilter?.criteria]);

	React.useEffect(() => {
		updateState({
			trigger,
		});
	}, [trigger]);

	const setTrigger = async (_trigger: Api.IAutomationTrigger, fallbackTrigger?: Api.IAutomationTrigger) => {
		if (!Api.isValidAutomationTrigger(_trigger)) {
			updateState({
				trigger: _trigger,
			});
			return;
		}
		try {
			const opResult = await automationTemplate?.addTrigger(_trigger);
			if (opResult?.success) {
				updateState({
					trigger: _trigger,
				});
				toast.success('Automation trigger set successfully');
			} else {
				errorMessages?.pushApiError(opResult);
			}
		} catch (err: any) {
			logApiError('SetTrigger-Error', err);
			errorMessages?.pushApiError(err);
			if (fallbackTrigger) {
				updateState({
					trigger: fallbackTrigger,
				});
			}
		}
	};

	const handleSaveCriteria = (criteria: Api.IContactFilterCriteria[]) => {
		if (!automationTemplate?.id) {
			return;
		}
		const newState: Partial<IState> = {
			viewCriteriaFlyout: false,
		};
		if (!criteria.length) {
			newState.advancedCancelOptions = false;
			return;
		}

		newState.cancelCriteria = criteria;
		updateCriteria.mutate({ id: automationTemplate.id, cancelCriteria: criteria });
		newState.advancedCancelOptions = true;
		updateState(newState);
	};

	const updateState = (newState: Partial<IState>) => {
		setState(prevState => ({
			...prevState,
			...newState,
		}));
	};

	const onDismissFlyout = () => {
		const nextState: Partial<IState> = {
			viewCriteriaFlyout: false,
		};
		if (!state.cancelCriteria?.length) {
			nextState.advancedCancelOptions = false;
		}
		updateState(nextState);
	};

	const handleAdvancedCancelOptionsChange = async () => {
		if (!automationTemplate?.id) {
			return;
		}
		const nextState: Partial<IState> = {
			advancedCancelOptions: !state.advancedCancelOptions,
		};
		if (!state.advancedCancelOptions && !state.cancelCriteria?.length) {
			nextState.viewCriteriaFlyout = true;
		}
		if (!nextState.advancedCancelOptions && state.cancelCriteria?.length) {
			updateCriteria.mutate({ id: automationTemplate.id, cancelCriteria: [] });

			nextState.cancelCriteria = [];
		}
		updateState(nextState);
	};

	const handleSendToHouseholdCheckChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
		sendToHouseholdMutation.mutate({
			id: automationTemplate.id,
			sendToHousehold: ev.target.checked,
			impersonationContext: automationTemplate?.impersonationContext,
		});
	};

	const handleAllowUnsubscribedCheckChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
		allowUnsubscribedMutation.mutate({
			id: automationTemplate.id,
			allowUnsubscribed: ev.target.checked,
			impersonationContext: automationTemplate?.impersonationContext,
		});
	};

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} automation-trigger-card ${className || ''}`}>
			<article className={css(styleSheet.cardHeader)}>
				<h4 className={css(styleSheet.cardHeaderText)}>Automation Type:</h4>
				{automationTemplate?.isLoaded ? (
					<div className={css(baseStyleSheet.horizontalStack)}>
						<AutomationTriggerIcon trigger={state.trigger} />
						<div>
							{Api.VmUtils.Automations.triggers.getAutomationTriggerTitle(
								state.trigger,

								automationTemplate?.isImpersonating ? automationTemplate?.impersonationContext : userSession
							)}
						</div>
					</div>
				) : (
					<LoadingSpinner type='small' />
				)}
			</article>
			<div className={css(styleSheet.cardBody)}>
				{automationTemplate?.isLoaded ? (
					<>
						{!!Api.VmUtils.Automations.triggers.triggerHasOptions(state.trigger) && (
							<AutomationTriggerSettings
								automationTemplate={automationTemplate}
								onRequestSetTrigger={setTrigger}
								trigger={state.trigger}
							/>
						)}
						<AutomationCancelOnReplyCheckbox automationTemplate={automationTemplate} />
						{(
							automationTemplate?.isImpersonating
								? automationTemplate?.impersonationContext?.account?.features?.households?.enabled
								: userSession.account.features.households?.enabled
						) ? (
							<Checkbox
								checked={automationTemplate?.sendToHousehold === true}
								className={css(styleSheet.sendToHouseholdsCheckbox)}
								disabled={sendToHouseholdMutation.isPending}
								id='automation-send-to-household-checkbox'
								onChange={handleSendToHouseholdCheckChange}
								type='large'
							>
								<div>
									<div className={css(styleSheet.checkboxText)}>Send to Households for this automation.</div>
								</div>
							</Checkbox>
						) : null}
					</>
				) : null}
				<div>
					<AdvancedOptionsButton
						isCollapsed={!state.advancedOptionsToggled}
						onClick={() =>
							updateState({
								advancedOptionsToggled: !state.advancedOptionsToggled,
							})
						}
					/>
					<Collapsible isCollapsed={!state.advancedOptionsToggled}>
						<>
							<Checkbox
								id='advancedCancelOptions'
								checked={state.advancedCancelOptions}
								type='large'
								onChange={handleAdvancedCancelOptionsChange}
							>
								<div>
									<p className={css(styleSheet.noMargin, styleSheet.checkboxText)}>
										Cancel if contact no longer meets criteria
									</p>
								</div>
							</Checkbox>
							{state.cancelCriteria?.length ? (
								<button className={css(styleSheet.ctaButton)} onClick={() => onViewCriteria(true)}>
									View Criteria
								</button>
							) : null}
							{/* Show allowUnsubscribed option only for manual automations.
								Manual automations have no trigger (_type is undefined), while other 
								automation types have specific triggers like NewClient, Tag, etc. */}
							{!state.trigger?._type ? (
								<>
									<Checkbox
										checked={!!automationTemplate?.allowUnsubscribed}
										className={css(styleSheet.allowUnsubscribedCheckbox)}
										disabled={allowUnsubscribedMutation.isPending}
										id='automation-allow-unsubscribed-checkbox'
										onChange={handleAllowUnsubscribedCheckChange}
										type='large'
										aria-describedby='automation-allow-unsubscribed-helper'
									>
										<div>
											<div className={css(styleSheet.checkboxText)}>Allow sending to unsubscribed contacts</div>
										</div>
									</Checkbox>
									<div id='automation-allow-unsubscribed-helper' className={css(styleSheet.checkboxHelperText)}>
										Enable ONLY for critical transactional communications to stay in compliance with CAN-SPAM.
									</div>
								</>
							) : null}
						</>
					</Collapsible>
				</div>
			</div>
			<FlyoutModal useDefaultHeader={false} onRequestClose={onDismissFlyout} isOpen={state.viewCriteriaFlyout}>
				<AdvancedFiltersContextProvider
					initialFilterCriteria={state.cancelCriteria}
					refresh={state.viewCriteriaFlyout}
					onSave={handleSaveCriteria}
				>
					<AdvancedFiltersLegacyUI onDismissFlyout={onDismissFlyout} />
				</AdvancedFiltersContextProvider>
			</FlyoutModal>
		</div>
	);
};

export const AutomationTriggerCard = observer(_AutomationTriggerCard);
